import { DateObject, noop, pxToDP, type TransactionSummaryDTO, useIntl } from '@capsicum/common';
import { Button, Image, ListCard, type StaticImage } from '@capsicum/common-web';
import { StatusType, useTransactionCard } from '@capsicum/features/wallet/shared';
import { type MouseEvent, useCallback } from 'react';
import styled, { css, type RuleSet, useTheme } from 'styled-components';

import { type OrderCancellation } from '../../hooks';

function extractTime(dateTime: string): string {
	const time = new DateObject(dateTime);

	return time.format('HH:mm');
}

export type TransactionCardProps = TransactionSummaryDTO & {
	cardStyle?: RuleSet<object>;
	onClick?: () => void;
	orderCancellation?: OrderCancellation;
	partnerLogoUrl?: string;
};

export const TransactionCard = ({
	amount,
	cardStyle,
	currencyShortCode,
	date,
	isNegativeBalance,
	onClick,
	orderCancellation = { handleClickCancelOrder: noop, isCancellable: false },
	partnerLogoUrl,
	status,
	transactionId,
	transactionType,
	transactionTypeName
}: TransactionCardProps) => {
	const { $t } = useIntl();
	const { sizing } = useTheme();
	const { formattedCurrency, isCurrencyRP, partnerLogoOrPlaceholderUrl } = useTransactionCard({
		amount,
		currencyShortCode,
		isNegativeBalance,
		partnerLogoUrl
	});

	const { handleClickCancelOrder, isCancellable } = orderCancellation;

	const onClickCancelOrder = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();
			handleClickCancelOrder();
		},
		[handleClickCancelOrder]
	);

	return (
		<S.Card
			$cardStyle={cardStyle}
			$isCancellable={isCancellable}
			testID={`transaction-${transactionId}`}
			onClick={onClick}
		>
			<S.ListCardStart $isCancellable={isCancellable}>
				{isCurrencyRP ? (
					<Image
						alt="Reward Points logo"
						height={pxToDP(sizing.xlarge)}
						src={partnerLogoOrPlaceholderUrl as StaticImage['src']}
					/>
				) : (
					<ListCard.Image src={partnerLogoOrPlaceholderUrl} />
				)}
			</S.ListCardStart>
			<S.ListCardContent $isCancellable={isCancellable}>
				<ListCard.Row>
					<S.TransactionTitle>{transactionTypeName || transactionType}</S.TransactionTitle>
					<S.AmountTitle>{formattedCurrency}</S.AmountTitle>
				</ListCard.Row>
				<ListCard.Row>
					<ListCard.SubTitle style={{ flex: 1 }}>{date ? extractTime(date) : 'No date'}</ListCard.SubTitle>
					<S.StatusDot $status={status as StatusType} />
					<S.Status>{status}</S.Status>
				</ListCard.Row>
				{isCancellable && (
					<ListCard.Row>
						<S.CancelOrderButton
							testID="transaction-cancel-order-button"
							text={$t('wallet.transactionHistory.cancelOrder')}
							textSize="xsmall"
							onClick={onClickCancelOrder}
						/>
					</ListCard.Row>
				)}
			</S.ListCardContent>
		</S.Card>
	);
};

namespace S {
	export const ListCardStart = styled(ListCard.Start)<{ $isCancellable?: boolean }>`
		align-self: ${({ $isCancellable }) => ($isCancellable ? 'flex-start' : 'center')};
		padding: ${({ theme }) => theme.spacings.small};
	`;

	export const ListCardContent = styled(ListCard.Content)<{ $isCancellable?: boolean }>`
		${({ $isCancellable }) =>
			$isCancellable &&
			css`
				padding-bottom: ${({ theme }) => theme.spacings.xxsmall};
			`}
	`;

	export const Card = styled(ListCard)<{ $cardStyle?: RuleSet<object>; $isCancellable?: boolean }>`
		margin-bottom: ${({ theme }) => theme.spacings.small};
		padding-right: ${({ theme }) => theme.spacings.medium};
		height: auto;
		cursor: pointer;

		&:last-child {
			margin-bottom: 16px;
		}

		${({ $cardStyle }) => $cardStyle};
	`;

	export const Status = styled(ListCard.SubTitle)`
		${({ theme }) => theme.fonts.text.caption1};
		line-height: 13px;
		letter-spacing: 0;
		text-align: left;
		text-transform: capitalize;
	`;

	export const AmountTitle = styled(ListCard.Title)`
		${({ theme }) => theme.fonts.text.text1};
		line-height: 21px;
		letter-spacing: 0;
		text-align: right;
		width: auto;
	`;

	export const TransactionTitle = styled(ListCard.Title)`
		width: auto;
		text-transform: capitalize;
	`;

	export const StatusDot = styled.div<{ $status: StatusType }>`
		width: 8px;
		height: 8px;
		${({ theme }) => theme.border.radius.circle};
		background-color: ${({ $status, theme }) => {
			switch ($status.toLowerCase()) {
				case StatusType.Processing:
					return theme.colors.backgrounds.status.partiallyCorrect;
				case StatusType.Processed:
					return theme.colors.backgrounds.status.available;
				default:
					return theme.colors.backgrounds.status.incorrect;
			}
		}};
		margin-right: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const CancelOrderButton = styled(Button)`
		height: 24px;
	`;
}
